<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { VuetifyTiptap } from 'vuetify-pro-tiptap'
import { tiptapEditorProps } from '@/plugins/tiptap'
import axiosIns from '@/plugins/axios'
import EntityPicker from '@/@core/components/admin/EntityPicker.vue'

// ── State ────────────────────────────────────────────────────────────
const activeTab = ref('compose')
const snackbar = ref({ show: false, message: '', color: 'success' })

// Content blocks are authored as rich HTML via TipTap.
const tiptapOutput = ref('html')

const meta = ref({ senders: [], audiences: [], mergeFields: [] })

// Compose form
const form = ref({
  senderKey: 'kapil',
  audienceType: 'allPlatinum',
  dateStart: '',
  content: {
    subject: '',
    introCopy: '',
    newImproved: '',
    recentlyBookedIntro: '',
    referralBlock: '',
    closing: '',
  },
})

// Test-list recipients: user objects chosen via the member search picker.
// Only their emails are sent to the backend (it re-resolves emails → users).
const testRecipients = ref([])

const audienceCount = ref(null)
const audienceCountLoading = ref(false)
const bookedGroups = ref([])
const bookedLoading = ref(false)

const previewHtml = ref('')
const previewMemberEmail = ref('')
const previewLoading = ref(false)

const testEmail = ref('')
const sending = ref(false)
const savingTemplate = ref(false)
const testing = ref(false)
const confirmSend = ref(false)

// Campaigns
const campaigns = ref([])
const campaignsLoading = ref(false)
const selectedAnalytics = ref(null)
const analyticsLoading = ref(false)

// Per-user click activity for the selected campaign
const clickFeed = ref({ rows: [], nextCursor: null, total: 0 })
const clicksLoading = ref(false)
const clicksLoadingMore = ref(false)

// ── Derived ──────────────────────────────────────────────────────────
const isTestList = computed(() => form.value.audienceType === 'testList')

const bookedRestaurantCount = computed(() =>
  bookedGroups.value.reduce((sum, g) => sum + (g.restaurants?.length || 0), 0))

const sendDisabled = computed(() =>
  sending.value || !form.value.content.subject?.trim()
  || (isTestList.value && customEmailList().length === 0))

// ── Helpers ──────────────────────────────────────────────────────────
function notify(message, color = 'success') {
  snackbar.value = { show: true, message, color }
}

function customEmailList() {
  return testRecipients.value
    .map(u => (u?.email || '').trim())
    .filter(Boolean)
}

// Default "Booked since" date: the first day of the prior month. The end of
// the range is always "now" — handled server-side — so we only track a start.
function defaultStartDate() {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  
  return start.toISOString().slice(0, 10)
}

function pct(n) {
  return `${(Number(n || 0) * 100).toFixed(1)}%`
}

// Render a merge-field name as its literal tag, e.g. firstName -> {{firstName}}.
// Built in script (not the template) to avoid nested-mustache parse errors.
function mergeTag(field) {
  return ['{{', field, '}}'].join('')
}

// Short descriptions for the merge-field cheat sheet.
const mergeFieldHelp = {
  firstName: "Member's first name",
  fullName: "Member's full name",
  referralLink: 'Personal referral link',
  membershipType: 'e.g. Platinum',
}

// Copy a merge tag to the clipboard so the admin can paste it into any block.
// Falls back to a hidden-textarea + execCommand when the async Clipboard API
// is unavailable (older browsers, non-secure contexts).
async function copyMergeField(field) {
  const tag = mergeTag(field)
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(tag)
    } else {
      const ta = document.createElement('textarea')
      ta.value = tag
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    notify(`Copied ${tag}`)
  } catch {
    notify('Copy failed — select the tag and copy manually', 'warning')
  }
}

// ── Data loading ─────────────────────────────────────────────────────
async function loadMeta() {
  const { data } = await axiosIns.get('/admin/member-updates/meta')
  if (data.success) meta.value = data.data
}

async function loadTemplate() {
  const { data } = await axiosIns.get('/admin/member-updates/template')
  if (data.success && data.data) {
    const t = data.data
    form.value.senderKey = t.senderKey || 'kapil'
    form.value.content = {
      subject: t.subject || '',
      introCopy: t.introCopy || '',
      newImproved: t.newImproved || '',
      recentlyBookedIntro: t.recentlyBookedIntro || '',
      referralBlock: t.referralBlock || '',
      closing: t.closing || '',
    }
  }
}

async function refreshAudienceCount() {
  if (isTestList.value) {
    audienceCount.value = customEmailList().length
    
    return
  }
  audienceCountLoading.value = true
  try {
    const { data } = await axiosIns.get('/admin/member-updates/audience-count', {
      params: { type: form.value.audienceType },
    })
    if (data.success) audienceCount.value = data.data.count
  } catch (err) {
    audienceCount.value = null
  } finally {
    audienceCountLoading.value = false
  }
}

async function refreshBooked() {
  bookedLoading.value = true
  try {
    const { data } = await axiosIns.get('/admin/member-updates/recently-booked', {
      params: { start: form.value.dateStart },
    })
    if (data.success) bookedGroups.value = data.data
  } finally {
    bookedLoading.value = false
  }
}

async function refreshPreview() {
  previewLoading.value = true
  try {
    const { data } = await axiosIns.post('/admin/member-updates/preview', {
      content: form.value.content,
      dateRange: { start: form.value.dateStart },
      memberEmail: previewMemberEmail.value || undefined,
    })
    if (data.success) previewHtml.value = data.data.html
  } catch (err) {
    notify('Preview failed', 'error')
  } finally {
    previewLoading.value = false
  }
}

// ── Actions ──────────────────────────────────────────────────────────
async function saveTemplate() {
  savingTemplate.value = true
  try {
    await axiosIns.put('/admin/member-updates/template', {
      senderKey: form.value.senderKey,
      ...form.value.content,
    })
    notify('Template saved')
  } catch (err) {
    notify('Save failed', 'error')
  } finally {
    savingTemplate.value = false
  }
}

async function sendTest() {
  if (!testEmail.value.trim()) return notify('Enter a test email address', 'warning')
  testing.value = true
  try {
    await axiosIns.post('/admin/member-updates/test', {
      recipientEmail: testEmail.value.trim(),
      senderKey: form.value.senderKey,
      content: form.value.content,
      dateRange: { start: form.value.dateStart },
    })
    notify(`Test email queued to ${testEmail.value.trim()}`)
  } catch (err) {
    notify('Test send failed', 'error')
  } finally {
    testing.value = false
  }
}

async function sendCampaign() {
  confirmSend.value = false
  sending.value = true
  try {
    const { data } = await axiosIns.post('/admin/member-updates/campaigns', {
      senderKey: form.value.senderKey,
      audienceType: form.value.audienceType,
      customEmails: isTestList.value ? customEmailList() : [],
      dateRange: { start: form.value.dateStart },
      content: form.value.content,
    })
    if (data.success) {
      notify(`Campaign sent — ${data.data.recipientCount} recipients queued`)
      activeTab.value = 'campaigns'
      loadCampaigns()
    }
  } catch (err) {
    notify(err.response?.data?.error || 'Send failed', 'error')
  } finally {
    sending.value = false
  }
}

async function loadCampaigns() {
  campaignsLoading.value = true
  try {
    const { data } = await axiosIns.get('/admin/member-updates/campaigns')
    if (data.success) campaigns.value = data.data
  } finally {
    campaignsLoading.value = false
  }
}

async function openAnalytics(campaign) {
  analyticsLoading.value = true
  selectedAnalytics.value = null
  clickFeed.value = { rows: [], nextCursor: null, total: 0 }
  try {
    const { data } = await axiosIns.get(`/admin/member-updates/campaigns/${campaign._id}/analytics`)
    if (data.success) selectedAnalytics.value = data.data
    await loadClicks({ reset: true })
  } finally {
    analyticsLoading.value = false
  }
}

async function loadClicks({ reset = false } = {}) {
  const campaign = selectedAnalytics.value?.campaign
  if (!campaign?._id) return
  const flag = reset ? clicksLoading : clicksLoadingMore
  flag.value = true
  try {
    const params = { limit: 50 }
    if (!reset && clickFeed.value.nextCursor) params.before = clickFeed.value.nextCursor
    const { data } = await axiosIns.get(
      `/admin/member-updates/campaigns/${campaign._id}/clicks`,
      { params },
    )
    if (data.success) {
      clickFeed.value = reset
        ? data.data
        : {
          rows: [...clickFeed.value.rows, ...data.data.rows],
          nextCursor: data.data.nextCursor,
          total: data.data.total,
        }
    }
  } finally {
    flag.value = false
  }
}

function recipientLabel(recipient) {
  const name = `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim()
  
  return name || recipient.email || 'Unknown recipient'
}

function clickDestinationLabel(row) {
  if (row.restaurantName) return row.restaurantName
  if (row.linkType === 'referral') return 'Referral link'
  
  return row.targetUrl || 'Link'
}

const RELATIVE_TIME_UNITS = [
  { limit: 60, divisor: 1, suffix: 's' },
  { limit: 3600, divisor: 60, suffix: 'm' },
  { limit: 86400, divisor: 3600, suffix: 'h' },
  { limit: 2592000, divisor: 86400, suffix: 'd' },
]

function relativeTime(iso) {
  if (!iso) return ''
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  for (const unit of RELATIVE_TIME_UNITS) {
    if (sec < unit.limit) return `${Math.floor(sec / unit.divisor)}${unit.suffix} ago`
  }
  
  return new Date(iso).toLocaleDateString()
}

function fullTimestamp(iso) {
  return iso ? new Date(iso).toLocaleString() : ''
}

// ── Watchers ─────────────────────────────────────────────────────────
watch(() => [form.value.audienceType, testRecipients.value], refreshAudienceCount, { deep: true })
watch(() => form.value.dateStart, () => {
  if (form.value.dateStart) refreshBooked()
})
watch(activeTab, tab => { if (tab === 'campaigns') loadCampaigns() })

// ── Init ─────────────────────────────────────────────────────────────
onMounted(async () => {
  form.value.dateStart = defaultStartDate()
  await Promise.all([loadMeta(), loadTemplate()])
  await Promise.all([refreshAudienceCount(), refreshBooked()])
  refreshPreview()
})
</script>

<template>
  <div class="member-updates">
    <header class="page-head">
      <h1 class="page-title">
        Member Update Emails
      </h1>
      <p class="page-sub">
        Send personalized RM updates as individual emails — with referral links and recently booked restaurants.
      </p>
    </header>

    <VTabs
      v-model="activeTab"
      class="mb-6"
    >
      <VTab value="compose">
        Compose &amp; Send
      </VTab>
      <VTab value="campaigns">
        Sent Campaigns
      </VTab>
    </VTabs>

    <!-- ══ COMPOSE ══════════════════════════════════════════════════ -->
    <VWindow v-model="activeTab">
      <VWindowItem value="compose">
        <VRow>
          <!-- Editor column -->
          <VCol
            cols="12"
            md="7"
          >
            <section class="block">
              <h2 class="block-title">
                Audience &amp; Sender
              </h2>
              <div class="field-grid">
                <VSelect
                  v-model="form.senderKey"
                  label="Send from"
                  :items="meta.senders"
                  item-title="displayName"
                  item-value="key"
                  variant="outlined"
                  density="comfortable"
                />
                <VSelect
                  v-model="form.audienceType"
                  label="Audience"
                  :items="meta.audiences"
                  item-title="label"
                  item-value="type"
                  variant="outlined"
                  density="comfortable"
                />
              </div>
              <EntityPicker
                v-if="isTestList"
                v-model="testRecipients"
                type="member"
                multiple
                label="Test recipients"
                placeholder="Search members by name or email"
                density="comfortable"
                class="mt-3"
              />
              <div class="audience-count">
                <VProgressCircular
                  v-if="audienceCountLoading"
                  indeterminate
                  size="16"
                  width="2"
                />
                <template v-else>
                  <strong>{{ audienceCount ?? '—' }}</strong> recipient<span v-if="audienceCount !== 1">s</span>
                </template>
              </div>
            </section>

            <section class="block">
              <h2 class="block-title">
                Recently Booked range
              </h2>
              <p class="block-hint">
                Confirmed bookings (accepted, experienced, or canceled) with a reservation date on or after this date — including upcoming ones — are listed, grouped by city.
              </p>
              <div class="field-grid">
                <VTextField
                  v-model="form.dateStart"
                  type="date"
                  label="Booked since"
                  variant="outlined"
                  density="comfortable"
                />
              </div>
              <div class="audience-count">
                <VProgressCircular
                  v-if="bookedLoading"
                  indeterminate
                  size="16"
                  width="2"
                />
                <template v-else>
                  <strong>{{ bookedRestaurantCount }}</strong> restaurants across
                  <strong>{{ bookedGroups.length }}</strong> cities
                </template>
              </div>
            </section>

            <section class="block">
              <h2 class="block-title">
                Email content
              </h2>
              <div class="merge-cheatsheet">
                <span class="block-hint mb-0">Merge fields — click to copy, then paste into any block:</span>
                <div class="merge-chips">
                  <button
                    v-for="f in meta.mergeFields"
                    :key="f"
                    type="button"
                    class="merge-chip"
                    @click="copyMergeField(f)"
                  >
                    <code>{{ mergeTag(f) }}</code>
                    <span class="merge-chip-desc">{{ mergeFieldHelp[f] || '' }}</span>
                    <VIcon
                      icon="mdi-content-copy"
                      size="14"
                      class="merge-chip-icon"
                    />
                  </button>
                </div>
              </div>

              <div class="editor-field">
                <label class="editor-label">Subject</label>
                <VTextField
                  v-model="form.content.subject"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                />
              </div>

              <div class="editor-field">
                <label class="editor-label">Intro copy</label>
                <VuetifyTiptap
                  v-model="form.content.introCopy"
                  :output="tiptapOutput"
                  :editor-props="tiptapEditorProps"
                  outlined
                  rounded
                  :max-height="200"
                />
              </div>

              <div class="editor-field">
                <label class="editor-label">New / Improved</label>
                <VuetifyTiptap
                  v-model="form.content.newImproved"
                  :output="tiptapOutput"
                  :editor-props="tiptapEditorProps"
                  outlined
                  rounded
                  :max-height="240"
                />
              </div>

              <div class="editor-field">
                <label class="editor-label">Referral block</label>
                <VuetifyTiptap
                  v-model="form.content.referralBlock"
                  :output="tiptapOutput"
                  :editor-props="tiptapEditorProps"
                  outlined
                  rounded
                  :max-height="200"
                />
              </div>

              <div class="editor-field">
                <label class="editor-label">Recently Booked intro line</label>
                <VuetifyTiptap
                  v-model="form.content.recentlyBookedIntro"
                  :output="tiptapOutput"
                  :editor-props="tiptapEditorProps"
                  outlined
                  rounded
                  :max-height="180"
                />
              </div>

              <div class="editor-field">
                <label class="editor-label">Closing</label>
                <VuetifyTiptap
                  v-model="form.content.closing"
                  :output="tiptapOutput"
                  :editor-props="tiptapEditorProps"
                  outlined
                  rounded
                  :max-height="180"
                />
              </div>
            </section>

            <section class="block secondary-actions">
              <VBtn
                variant="text"
                :loading="savingTemplate"
                prepend-icon="mdi-content-save"
                @click="saveTemplate"
              >
                Save template
              </VBtn>
              <div class="test-send">
                <VTextField
                  v-model="testEmail"
                  placeholder="you@reserverm.com"
                  label="Test address"
                  variant="outlined"
                  density="compact"
                  hide-details
                />
                <VBtn
                  variant="tonal"
                  :loading="testing"
                  prepend-icon="mdi-send-check"
                  @click="sendTest"
                >
                  Send test
                </VBtn>
              </div>
            </section>
          </VCol>

          <!-- Preview + send column -->
          <VCol
            cols="12"
            md="5"
          >
            <div class="preview-pane">
              <div class="preview-head">
                <h2 class="block-title mb-0">
                  Preview
                </h2>
                <VBtn
                  size="small"
                  variant="text"
                  :loading="previewLoading"
                  prepend-icon="mdi-refresh"
                  @click="refreshPreview"
                >
                  Refresh
                </VBtn>
              </div>
              <VTextField
                v-model="previewMemberEmail"
                label="Preview as member (email — optional)"
                placeholder="leave blank for a sample member"
                variant="outlined"
                density="compact"
                class="mb-3"
                @keyup.enter="refreshPreview"
              />
              <iframe
                v-if="previewHtml"
                :srcdoc="previewHtml"
                class="preview-frame"
                title="Email preview"
              />
              <div
                v-else
                class="preview-empty"
              >
                Edit content and refresh to preview.
              </div>

              <VBtn
                block
                size="large"
                color="primary"
                class="send-cta"
                :disabled="sendDisabled"
                :loading="sending"
                prepend-icon="mdi-email-fast"
                @click="confirmSend = true"
              >
                Send to {{ audienceCount ?? '…' }} member{{ audienceCount === 1 ? '' : 's' }}
              </VBtn>
            </div>
          </VCol>
        </VRow>
      </VWindowItem>

      <!-- ══ CAMPAIGNS ══════════════════════════════════════════════ -->
      <VWindowItem value="campaigns">
        <VRow>
          <VCol
            cols="12"
            md="5"
          >
            <h2 class="block-title">
              Sent campaigns
            </h2>
            <VProgressLinear
              v-if="campaignsLoading"
              indeterminate
              class="mb-3"
            />
            <p
              v-else-if="!campaigns.length"
              class="block-hint"
            >
              No campaigns sent yet.
            </p>
            <ul class="campaign-list">
              <li
                v-for="c in campaigns"
                :key="c._id"
                class="campaign-row"
                :class="{ active: selectedAnalytics?.campaign?._id === c._id }"
                tabindex="0"
                @click="openAnalytics(c)"
                @keyup.enter="openAnalytics(c)"
              >
                <div class="campaign-row-main">
                  <span class="campaign-subject">{{ c.content?.subject || '(no subject)' }}</span>
                  <VChip
                    size="x-small"
                    :color="c.status === 'sent' ? 'success' : 'warning'"
                    label
                  >
                    {{ c.status }}
                  </VChip>
                </div>
                <div class="campaign-row-meta">
                  {{ new Date(c.sentAt || c.createdAt).toLocaleDateString() }}
                  · {{ c.recipientCount }} recipients
                </div>
              </li>
            </ul>
          </VCol>

          <VCol
            cols="12"
            md="7"
          >
            <VProgressLinear
              v-if="analyticsLoading"
              indeterminate
            />
            <div
              v-else-if="selectedAnalytics"
              class="analytics"
            >
              <h2 class="block-title">
                {{ selectedAnalytics.campaign.content?.subject }}
              </h2>
              <div class="metric-grid">
                <div class="metric">
                  <span class="metric-value">{{ selectedAnalytics.delivery.delivered }}</span>
                  <span class="metric-label">Delivered</span>
                </div>
                <div class="metric">
                  <span class="metric-value">{{ selectedAnalytics.opens.unique }}</span>
                  <span class="metric-label">Unique opens · {{ pct(selectedAnalytics.opens.rate) }}</span>
                </div>
                <div class="metric">
                  <span class="metric-value">{{ selectedAnalytics.clicks.unique }}</span>
                  <span class="metric-label">Unique clicks · {{ pct(selectedAnalytics.clicks.rate) }}</span>
                </div>
                <div class="metric">
                  <span class="metric-value">{{ selectedAnalytics.referralClicks }}</span>
                  <span class="metric-label">Referral clicks</span>
                </div>
              </div>

              <div class="rank-cols">
                <div>
                  <h3 class="rank-title">
                    Top restaurants
                  </h3>
                  <p
                    v-if="!selectedAnalytics.topRestaurants.length"
                    class="block-hint"
                  >
                    No clicks yet.
                  </p>
                  <ol class="rank-list">
                    <li
                      v-for="r in selectedAnalytics.topRestaurants"
                      :key="r.value"
                    >
                      <span>{{ r.label || r.value }}</span><strong>{{ r.count }}</strong>
                    </li>
                  </ol>
                </div>
                <div>
                  <h3 class="rank-title">
                    Top markets
                  </h3>
                  <p
                    v-if="!selectedAnalytics.topMarkets.length"
                    class="block-hint"
                  >
                    No clicks yet.
                  </p>
                  <ol class="rank-list">
                    <li
                      v-for="m in selectedAnalytics.topMarkets"
                      :key="m.value"
                    >
                      <span>{{ m.value }}</span><strong>{{ m.count }}</strong>
                    </li>
                  </ol>
                </div>
              </div>

              <section class="click-feed">
                <header class="click-feed-head">
                  <h3 class="rank-title">
                    Click activity
                  </h3>
                  <span class="click-feed-count">
                    {{ clickFeed.total }} click<span v-if="clickFeed.total !== 1">s</span>
                  </span>
                </header>
                <VProgressLinear
                  v-if="clicksLoading"
                  indeterminate
                />
                <p
                  v-else-if="!clickFeed.rows.length"
                  class="block-hint"
                >
                  No clicks recorded yet.
                </p>
                <ul
                  v-else
                  class="click-list"
                >
                  <li
                    v-for="row in clickFeed.rows"
                    :key="row.id"
                    class="click-row"
                  >
                    <div class="click-row-main">
                      <span class="click-recipient">{{ recipientLabel(row.recipient) }}</span>
                      <span class="click-arrow">→</span>
                      <a
                        v-if="row.targetUrl"
                        :href="row.targetUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="click-destination"
                      >{{ clickDestinationLabel(row) }}</a>
                      <span
                        v-else
                        class="click-destination"
                      >{{ clickDestinationLabel(row) }}</span>
                      <VChip
                        v-if="row.linkType"
                        size="x-small"
                        label
                        class="click-type"
                      >
                        {{ row.linkType }}
                      </VChip>
                    </div>
                    <div class="click-row-meta">
                      <span class="click-recipient-email">{{ row.recipient.email }}</span>
                      <span
                        v-if="row.market"
                        class="click-market"
                      >· {{ row.market }}</span>
                      <span
                        class="click-time"
                        :title="fullTimestamp(row.clickedAt)"
                      >{{ relativeTime(row.clickedAt) }}</span>
                    </div>
                  </li>
                </ul>
                <div
                  v-if="clickFeed.nextCursor"
                  class="click-more"
                >
                  <VBtn
                    variant="text"
                    size="small"
                    :loading="clicksLoadingMore"
                    @click="loadClicks"
                  >
                    Load more
                  </VBtn>
                </div>
              </section>
            </div>
            <div
              v-else
              class="preview-empty"
            >
              Select a campaign to see its analytics.
            </div>
          </VCol>
        </VRow>
      </VWindowItem>
    </VWindow>

    <!-- Send confirmation -->
    <VDialog
      v-model="confirmSend"
      max-width="440"
    >
      <VCard>
        <VCardTitle>Send this update?</VCardTitle>
        <VCardText>
          Each of the <strong>{{ audienceCount }}</strong> recipient<span v-if="audienceCount !== 1">s</span>
          will receive their own personalized email. This cannot be undone.
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmSend = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            @click="sendCampaign"
          >
            Send now
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="4000"
    >
      {{ snackbar.message }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.member-updates {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.page-head { margin-bottom: 16px; }
.page-title { font-size: 24px; font-weight: 700; margin: 0; }
.page-sub { font-size: 14px; color: rgba(0, 0, 0, 0.6); margin: 4px 0 0; }

.block { margin-bottom: 28px; }
.block-title { font-size: 16px; font-weight: 600; margin: 0 0 4px; }
.block-hint { font-size: 13px; color: rgba(0, 0, 0, 0.6); margin: 0 0 12px; }
.editor-field { margin-bottom: 16px; }
.editor-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 6px;
}

.block-hint code {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  padding: 1px 6px;
  margin-right: 6px;
  font-size: 12px;
}

/* Merge-field cheat sheet */
.merge-cheatsheet { margin-bottom: 16px; }
.merge-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.merge-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
  cursor: pointer;
  transition: background 150ms, border-color 150ms;
}
.merge-chip:hover { background: rgba(var(--v-theme-primary), 0.08); border-color: rgb(var(--v-theme-primary)); }
.merge-chip:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 1px; }
.merge-chip code {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}
.merge-chip-desc { font-size: 12px; color: rgba(0, 0, 0, 0.55); }
.merge-chip-icon { color: rgba(0, 0, 0, 0.4); }
.merge-chip:hover .merge-chip-icon { color: rgb(var(--v-theme-primary)); }

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.audience-count {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
  margin-top: 8px;
  min-height: 20px;
}
.audience-count strong { font-size: 16px; color: rgba(0, 0, 0, 0.9); }

.secondary-actions {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 16px;
}
.test-send {
  display: flex;
  align-items: center;
  gap: 8px;
}
.test-send :deep(.v-field) { min-width: 220px; }

.preview-pane {
  position: sticky;
  top: 16px;
}
.preview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.preview-frame {
  width: 100%;
  height: 460px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #fff;
}
.preview-empty {
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;
}
.send-cta { margin-top: 16px; }

/* Campaign list */
.campaign-list { list-style: none; padding: 0; margin: 0; }
.campaign-row {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 150ms, background 150ms;
}
.campaign-row:hover { background: rgba(0, 0, 0, 0.03); }
.campaign-row:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); }
.campaign-row.active { border-color: rgb(var(--v-theme-primary)); background: rgba(var(--v-theme-primary), 0.06); }
.campaign-row-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.campaign-subject { font-weight: 600; font-size: 14px; }
.campaign-row-meta { font-size: 12px; color: rgba(0, 0, 0, 0.6); margin-top: 2px; }

/* Analytics */
.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 16px 0 24px;
}
.metric {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 16px;
}
.metric-value { display: block; font-size: 28px; font-weight: 700; line-height: 1.1; }
.metric-label { font-size: 12px; color: rgba(0, 0, 0, 0.6); }

.rank-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.rank-title { font-size: 14px; font-weight: 600; margin: 0 0 8px; }
.rank-list { list-style: none; padding: 0; margin: 0; }
.rank-list li {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* Click activity feed */
.click-feed { margin-top: 32px; }
.click-feed-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}
.click-feed-count {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.click-list {
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.click-row {
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.click-row-main {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.3;
  flex-wrap: wrap;
}
.click-recipient { font-weight: 600; color: rgba(0, 0, 0, 0.88); }
.click-arrow { color: rgba(0, 0, 0, 0.35); }
.click-destination {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;
  word-break: break-word;
}
.click-destination:hover { text-decoration: underline; }
.click-type { margin-left: auto; text-transform: capitalize; }
.click-row-meta {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
}
.click-recipient-email { color: rgba(0, 0, 0, 0.5); }
.click-time { margin-left: auto; }
.click-more { display: flex; justify-content: center; margin-top: 8px; }

/* iOS: keep all inputs >= 16px so Safari doesn't zoom on focus. */
:deep(input),
:deep(textarea) { font-size: 16px; }

@media (max-width: 600px) {
  .field-grid { grid-template-columns: 1fr; }
  .metric-grid { grid-template-columns: 1fr; }
  .rank-cols { grid-template-columns: 1fr; }
  .secondary-actions { flex-direction: column; align-items: stretch; }
  .click-type { margin-left: 0; }
  .click-time { margin-left: 0; }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
</route>
